// src/i18n/index.js
import { createI18n } from 'vue-i18n'
import axios from 'axios'

// -------------------- Stałe --------------------
export const FALLBACK = 'pl-PL'
export const FALLBACK_UI = 'en-GB'
const FALLBACK_LIST = ['cs', 'de', 'en', 'hu', 'lt', 'pl', 'ro', 'ru', 'sk', 'fr', 'es']

// -------------------- Helpers --------------------
export function toBCP47(input) {
  const raw =
    (input && typeof input === 'object')
      ? (input.value ?? input.code ?? input.locale ?? input.lang ?? '')
      : (input ?? '')

  const str = String(raw).trim()
  if (!str) return FALLBACK

  const map = {
    cs: 'cs-CZ', de: 'de-DE', en: 'en-GB', es: 'es-ES', fr: 'fr-FR', hu: 'hu-HU',
    lt: 'lt-LT', pl: 'pl-PL', ro: 'ro-RO', ru: 'ru-RU', sk: 'sk-SK'
  }

  const lc = str.toLowerCase()

  if (map[lc]) return map[lc]

  if (lc.includes('-')) {
    const [lang, region] = lc.split('-')
    return `${lang}-${(region || '').toUpperCase()}`
  }

  if (lc.length === 2) return `${lc}-${lc.toUpperCase()}`

  return str
}

export function toLabel(tag) {
  try {
    const [lang, region] = String(tag).split('-')
    const dn = new Intl.DisplayNames([tag], { type: 'language' })
    const name = dn.of(lang) || lang
    return region ? `${name} (${region})` : name
  } catch {
    return String(tag)
  }
}

// -------------------- i18n instance --------------------
export const i18n = createI18n({
  legacy: false,
  locale: FALLBACK,          // nadpisujemy przy starcie
  fallbackLocale: FALLBACK_UI,
  messages: {}               // doładowujemy per widok
})

// -------------------- Ustawienie języka --------------------
export async function setLocale(locale) {
  const l = toBCP47(locale || i18n.global.locale.value || FALLBACK)

  i18n.global.locale.value = l

  const htmlLang = (typeof l === 'string' ? l : String(l)).split('-')[0]
  document.documentElement.lang = htmlLang

  axios.defaults.headers.common['Accept-Language'] = l
  try { localStorage.setItem('sv-lang', l) } catch { }
  return l
}

// -------------------- Lista języków z API (z memoizacją) --------------------
let _localesValue = null
let _localesPromise = null

export async function fetchLocaleOptions(force = false) {
  if (!force && _localesValue) return _localesValue
  if (!force && _localesPromise) return _localesPromise

  _localesPromise = (async () => {
    try {
      const { data } = await axios.get('/api/locales', {
        headers: { 'Accept-Language': i18n.global.locale.value || FALLBACK }
      })
      const arr = Array.isArray(data) ? data : []
      const opts = arr
        .map(c => toBCP47(c))
        .map(v => ({ value: v, label: toLabel(v) }))
      _localesValue = opts.length ? opts : defaultOptions()
      return _localesValue
    } catch (e) {
      console.warn('Locales fetch failed; using fallback list', e)
      _localesValue = defaultOptions()
      return _localesValue
    } finally {
      _localesPromise = null
    }
  })()

  return _localesPromise
}

export function clearLocalesCache() {
  _localesValue = null
  _localesPromise = null
}

function defaultOptions() {
  return FALLBACK_LIST
    .map(toBCP47)
    .map(v => ({ value: v, label: toLabel(v) }))
}

// -------------------- Per-view loader (Vite + webpack) --------------------

// Vite (import.meta.glob) — jeśli dostępny
let viteModules = null
try {
  if (typeof import.meta !== 'undefined' && typeof import.meta.glob === 'function') {
    // 2-poziomowy folder: views/<ViewName>/<locale>.json
    viteModules = import.meta.glob('./views/**/**/*.json')
  }
} catch { /* ignore */ }

// Webpack (require.context) — jeśli dostępny
let webpackCtx = null
try {
  // eslint-disable-next-line no-undef
  webpackCtx = require.context('./views', true, /\.json$/)
} catch { /* ignore */ }

// Cache już załadowanych plików per widok+locale, żeby nie ściągać/mergować drugi raz
const _loadedViewMsgs = new Set()

/**
 * Doładowuje tłumaczenia dla widoku i języka.
 * Szuka kolejno: locale -> FALLBACK -> FALLBACK_UI.
 * Zwraca true, jeśli coś załadował (albo było w cache).
 */
export async function loadViewLocale(viewName, locale) {
  const l = toBCP47(locale || i18n.global.locale.value || FALLBACK)
  const cacheKey = `${viewName}:${l}`

  if (_loadedViewMsgs.has(cacheKey)) return true

  const viteCandidates = [
    `./views/${viewName}/${l}.json`,
    `./views/${viewName}/${FALLBACK}.json`,
    `./views/${viewName}/${FALLBACK_UI}.json`
  ]
  const webpackCandidates = [
    `./${viewName}/${l}.json`,
    `./${viewName}/${FALLBACK}.json`,
    `./${viewName}/${FALLBACK_UI}.json`
  ]

  const loadAndMerge = (msgs) => {
    i18n.global.mergeLocaleMessage(l, msgs)
    const base = l.split('-')[0]
    if (base && base !== l) {
      i18n.global.mergeLocaleMessage(base, msgs)
    }
  }

  // Vite
  if (viteModules) {
    for (const p of viteCandidates) {
      const loader = viteModules[p]
      if (!loader) continue
      try {
        const mod = await loader()
        loadAndMerge(mod?.default || mod || {})
        _loadedViewMsgs.add(cacheKey)
        return true
      } catch (e) {
        console.warn(`[i18n] Failed to load ${p}:`, e)
      }
    }
  }

  // Webpack
  if (webpackCtx) {
    const keys = webpackCtx.keys()
    for (const k of webpackCandidates) {
      if (!keys.includes(k)) continue
      try {
        const mod = webpackCtx(k)
        loadAndMerge(mod?.default || mod || {})
        _loadedViewMsgs.add(cacheKey)
        return true
      } catch (e) {
        console.warn(`[i18n] Failed to load (webpack) ${k}:`, e)
      }
    }
  }

  console.warn(`[i18n] No locale file found for view "${viewName}" and locale "${l}"`)
  // nawet jeśli nie znaleźliśmy, żeby nie próbować w kółko — oznaczamy jako sprawdzone
  _loadedViewMsgs.add(cacheKey)
  return false
}

/**
 * Ustaw język i doładuj tłumaczenia danego widoku.
 */
export async function applyLocaleForView(viewName, locale) {
  const l = await setLocale(locale)
  await loadViewLocale(viewName, l)
  return l
}
